// ============================================================================
// EndosoCambioForm — captura el CAMBIO concreto de un endoso según su `target`.
// Devuelve (onChange) el objeto `cambios` que el motor (src/db/endosos.js) aplica.
// Fleet-aware: para exclusión/cambio/datos de vehículo (y suma/exclusión de
// cobertura) carga los vehículos/coberturas ACTUALES de la póliza para elegir cuál.
// ============================================================================
import React, { useEffect, useState } from 'react';
import { supabase } from '../db/engine';
import { getMoneda } from '../utils/moneda';
import CoberturaMiniList from './CoberturaMiniList';

const TIPOS_VEHICULO = ['Sedán', 'SUV', 'Pickup', 'Van', 'Camión', 'Motocicleta', 'Otro'];
const FORMAS_PAGO = ['ACH', 'TC', 'Yappy a Aseguradora', 'Aseguradora'];
const FRECUENCIAS = [
  ['mensual', 'Mensual'], ['bimensual', 'Bimensual'], ['trimestral', 'Trimestral'],
  ['semestral', 'Semestral'], ['anual', 'Anual'],
];
const VEH_FIELDS = ['anio', 'marca', 'modelo', 'color', 'tipo_vehiculo', 'pasajeros',
  'no_motor', 'chasis', 'chasis_igual_motor', 'placa', 'placa_por_reportar',
  'dueno_nombre', 'dueno_cedula'];

const labelVehiculo = (v) =>
  [v.marca, v.modelo, v.anio].filter(Boolean).join(' ') +
  (v.placa && v.placa !== 'Por reportar' ? ` — ${v.placa}` : (v.no_motor ? ` — motor ${v.no_motor}` : ''));

const labelCobertura = (c) =>
  c.nombre + (c.suma_asegurada != null ? ` — suma ${Number(c.suma_asegurada).toLocaleString()}` : '');

export default function EndosoCambioForm({ target, polizaId, value, onChange }) {
  const v = value || {};
  const set = (k, val) => onChange({ ...v, [k]: val });
  const setMany = (obj) => onChange({ ...v, ...obj });

  const [vehiculos, setVehiculos] = useState([]);
  const [coberturas, setCoberturas] = useState([]);
  const [unidades, setUnidades] = useState([]);

  const necesitaVehiculos = ['vehiculo_exclusion', 'vehiculo_cambio', 'vehiculo_datos'].includes(target);
  const necesitaCoberturas = ['suma_cambio', 'deducible_cambio', 'cobertura_exclusion'].includes(target);
  // Unidad genérica (ubicación / equipo / asegurado). tipo por prefijo del target.
  const unidadTipo = target && (target.startsWith('ubicacion_') ? 'ubicacion'
    : target.startsWith('equipo_') ? 'equipo'
    : target.startsWith('asegurado_') ? 'asegurado' : null);
  const esUnidadInclusion = !!unidadTipo && target.endsWith('_inclusion');
  const esUnidadExclusion = !!unidadTipo && target.endsWith('_exclusion');

  useEffect(() => {
    if (!polizaId) return;
    if (necesitaVehiculos) {
      supabase.from('poliza_autos').select('*').eq('poliza_id', polizaId).order('id')
        .then(({ data }) => setVehiculos(data || []));
    }
    if (necesitaCoberturas) {
      supabase.from('poliza_coberturas').select('*').eq('poliza_id', polizaId).order('id')
        .then(({ data }) => setCoberturas(data || []));
    }
    if (esUnidadExclusion) {
      supabase.from('poliza_unidades').select('*').eq('poliza_id', polizaId).eq('tipo', unidadTipo).order('id')
        .then(({ data }) => setUnidades(data || []));
    }
  }, [polizaId, target]); // eslint-disable-line

  // Al elegir un vehículo en "cambio de datos", precargar sus campos actuales para editar.
  // Se guarda también una etiqueta legible (para el documento impreso y el historial).
  const elegirVehiculo = (id) => {
    const veh = vehiculos.find(x => String(x.id) === String(id));
    const vehiculo_label = veh ? labelVehiculo(veh) : '';
    if (target === 'vehiculo_datos') {
      const pre = {}; if (veh) for (const k of VEH_FIELDS) pre[k] = veh[k] ?? '';
      // El check "dueño = contratante" se deriva de si hay propietario guardado.
      const dueno_igual = !(veh && (veh.dueno_nombre || veh.dueno_cedula));
      setMany({ vehiculo_id: id, vehiculo_label, dueno_igual, ...pre });
    } else {
      setMany({ vehiculo_id: id, vehiculo_label });
    }
  };
  const elegirCobertura = (id) => {
    const cob = coberturas.find(x => String(x.id) === String(id));
    setMany({ cobertura_id: id, cobertura_label: cob ? labelCobertura(cob) : '' });
  };

  // Sub-formulario de datos de vehículo (usado por inclusión / cambio / datos).
  const VehiculoFields = () => (
    <>
    <div className="form-grid form-grid-3">
      <div className="form-group"><label className="form-label">Año</label>
        <input className="form-input" type="number" value={v.anio || ''} onChange={e => set('anio', e.target.value)} /></div>
      <div className="form-group"><label className="form-label">Marca</label>
        <input className="form-input" value={v.marca || ''} onChange={e => set('marca', e.target.value)} /></div>
      <div className="form-group"><label className="form-label">Modelo</label>
        <input className="form-input" value={v.modelo || ''} onChange={e => set('modelo', e.target.value)} /></div>
      <div className="form-group"><label className="form-label">Color</label>
        <input className="form-input" value={v.color || ''} onChange={e => set('color', e.target.value)} /></div>
      <div className="form-group"><label className="form-label">Tipo</label>
        <select className="form-select" value={v.tipo_vehiculo || ''} onChange={e => set('tipo_vehiculo', e.target.value)}>
          <option value="">—</option>
          {TIPOS_VEHICULO.map(t => <option key={t} value={t}>{t}</option>)}
        </select></div>
      <div className="form-group"><label className="form-label">Pasajeros</label>
        <input className="form-input" type="number" value={v.pasajeros || ''} onChange={e => set('pasajeros', e.target.value)} /></div>
      <div className="form-group"><label className="form-label">N° Motor / VIN</label>
        <input className="form-input" value={v.no_motor || ''} onChange={e => set('no_motor', e.target.value)} /></div>
      <div className="form-group"><label className="form-label">Chasis</label>
        <input className="form-input" value={v.chasis_igual_motor ? (v.no_motor || '') : (v.chasis || '')}
          disabled={!!v.chasis_igual_motor} onChange={e => set('chasis', e.target.value)} /></div>
      <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
        <input type="checkbox" id="ecf-chasis-igual" checked={!!v.chasis_igual_motor}
          onChange={e => set('chasis_igual_motor', e.target.checked)} style={{ accentColor: 'var(--sky)', width: 15, height: 15 }} />
        <label htmlFor="ecf-chasis-igual" style={{ fontSize: 12, color: 'var(--silver)' }}>Chasis = Motor</label>
      </div>
      <div className="form-group"><label className="form-label">Placa</label>
        <input className="form-input" value={v.placa_por_reportar ? '' : (v.placa || '')}
          disabled={!!v.placa_por_reportar} placeholder={v.placa_por_reportar ? 'Por reportar' : ''}
          onChange={e => set('placa', e.target.value)} /></div>
      <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
        <input type="checkbox" id="ecf-placa-pr" checked={!!v.placa_por_reportar}
          onChange={e => set('placa_por_reportar', e.target.checked)} style={{ accentColor: 'var(--sky)', width: 15, height: 15 }} />
        <label htmlFor="ecf-placa-pr" style={{ fontSize: 12, color: 'var(--silver)' }}>Por reportar</label>
      </div>
    </div>
    <div style={{ marginTop: 10 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 600 }}>
        <input type="checkbox" checked={v.dueno_igual !== false}
          onChange={e => set('dueno_igual', e.target.checked)} style={{ accentColor: 'var(--sky)', width: 15, height: 15 }} />
        El dueño de esta unidad es el <span style={{ color: 'var(--sky-light)' }}>contratante de la póliza</span>
      </label>
      {v.dueno_igual === false && (
        <div className="form-grid form-grid-2" style={{ marginTop: 8 }}>
          <div className="form-group"><label className="form-label">Propietario (según RUV)</label>
            <input className="form-input" value={v.dueno_nombre || ''} onChange={e => set('dueno_nombre', e.target.value)} placeholder="Nombre del dueño del vehículo" /></div>
          <div className="form-group"><label className="form-label">Cédula / RUC del propietario</label>
            <input className="form-input" value={v.dueno_cedula || ''} onChange={e => set('dueno_cedula', e.target.value)} placeholder="8-888-8888" /></div>
        </div>
      )}
    </div>
    </>
  );

  const SelectorVehiculo = ({ label }) => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <select className="form-select" value={v.vehiculo_id || ''} onChange={e => elegirVehiculo(e.target.value)}>
        <option value="">— Selecciona el vehículo —</option>
        {vehiculos.map(x => <option key={x.id} value={x.id}>{labelVehiculo(x)}</option>)}
      </select>
      {vehiculos.length === 0 && <div style={{ fontSize: 11, color: 'var(--text-warning)', marginTop: 4 }}>Esta póliza no tiene vehículos cargados.</div>}
    </div>
  );

  // Unidades genéricas: inclusión (ubicación / equipo / asegurado).
  if (esUnidadInclusion) {
    const $m = getMoneda();
    const L = unidadTipo === 'ubicacion'
      ? { nombre: 'Ubicación / dirección del inmueble', id: null, valor: `Suma asegurada / valor (${$m})`, cob: true }
      : unidadTipo === 'equipo'
      ? { nombre: 'Descripción del equipo', id: 'Serie / placa / VIN', valor: `Valor de reposición (${$m})`, cob: true }
      : { nombre: 'Nombre del asegurado', id: 'Cédula', valor: `Suma asegurada (${$m})`, cob: false };
    return (<div>
      <div style={{ fontSize: 12, color: 'var(--silver)', marginBottom: 8 }}>Datos de {unidadTipo === 'asegurado' ? 'la persona' : 'la unidad'} que se INCLUYE:</div>
      <div className="form-grid form-grid-2">
        {unidadTipo === 'asegurado' ? (<>
          <div className="form-group"><label className="form-label">Nombre</label>
            <input className="form-input" value={v.nombre ?? ''} onChange={e => set('nombre', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Apellido</label>
            <input className="form-input" value={v.apellido ?? ''} onChange={e => set('apellido', e.target.value)} /></div>
        </>) : (
          <div className="form-group" style={{ gridColumn: L.id ? 'span 1' : 'span 2' }}>
            <label className="form-label">{L.nombre}</label>
            <input className="form-input" value={v.nombre ?? ''} onChange={e => set('nombre', e.target.value)} />
          </div>
        )}
        {L.id && (<div className="form-group"><label className="form-label">{L.id}</label>
          <input className="form-input" value={v.identificacion ?? ''} onChange={e => set('identificacion', e.target.value)} /></div>)}
        <div className="form-group"><label className="form-label">{L.valor}</label>
          <input className="form-input" type="number" step="0.01" value={v.valor ?? ''} onChange={e => set('valor', e.target.value)} placeholder="0.00" /></div>
      </div>
      {L.cob && (<div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed rgba(var(--silver-rgb),0.15)' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--silver)', letterSpacing: 0.5, marginBottom: 6 }}>COBERTURAS DE ESTA UNIDAD</div>
        <CoberturaMiniList value={v.coberturas || []} onChange={cobs => set('coberturas', cobs)} />
      </div>)}
      {unidadTipo === 'asegurado' && (() => {
        const deps = v.dependientes || [];
        const setDeps = (arr) => set('dependientes', arr);
        const upd = (i, k, val) => setDeps(deps.map((d, j) => j === i ? { ...d, [k]: val } : d));
        return (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed rgba(var(--silver-rgb),0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--silver)', letterSpacing: 0.5 }}>DEPENDIENTES DE ESTE ASEGURADO (opcional)</div>
              <button type="button" onClick={() => setDeps([...deps, { nombre: '', identificacion: '', parentesco: '' }])}
                style={{ fontSize: 11, fontWeight: 600, color: 'var(--sky-light)', background: 'none', border: '1px solid rgba(var(--sky-rgb),0.4)', borderRadius: 6, padding: '3px 9px', cursor: 'pointer' }}>+ Agregar dependiente</button>
            </div>
            {deps.length === 0 && <div style={{ fontSize: 11, color: 'var(--silver)' }}>Sin dependientes. Usa "+ Agregar" para incluir cónyuge, hijos, etc.</div>}
            {deps.map((d, i) => (
              <div key={i} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: i < deps.length - 1 ? '1px dashed rgba(var(--silver-rgb),0.12)' : 'none' }}>
                <div className="form-grid form-grid-2" style={{ alignItems: 'end' }}>
                  <div className="form-group" style={{ margin: 0 }}><label className="form-label">Nombre</label>
                    <input className="form-input" value={d.nombre || ''} onChange={e => upd(i, 'nombre', e.target.value)} /></div>
                  <div className="form-group" style={{ margin: 0 }}><label className="form-label">Apellido</label>
                    <input className="form-input" value={d.apellido || ''} onChange={e => upd(i, 'apellido', e.target.value)} /></div>
                  <div className="form-group" style={{ margin: 0 }}><label className="form-label">Cédula</label>
                    <input className="form-input" value={d.identificacion || ''} onChange={e => upd(i, 'identificacion', e.target.value)} placeholder="8-888-8888" /></div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'end' }}>
                    <div className="form-group" style={{ margin: 0, flex: 1 }}><label className="form-label">Parentesco</label>
                      <input className="form-input" value={d.parentesco || ''} onChange={e => upd(i, 'parentesco', e.target.value)} placeholder="Cónyuge / Hijo(a)" /></div>
                    <button type="button" onClick={() => setDeps(deps.filter((_, j) => j !== i))} title="Quitar"
                      style={{ background: 'none', border: '1px solid rgba(239, 68, 68, 0.4)', color: 'var(--text-danger)', borderRadius: 6, padding: '7px 9px', cursor: 'pointer', height: 34 }}>✕</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      })()}
    </div>);
  }
  if (esUnidadExclusion) {
    const label = unidadTipo === 'ubicacion' ? 'Ubicación a EXCLUIR' : unidadTipo === 'equipo' ? 'Equipo a EXCLUIR' : 'Asegurado a EXCLUIR';
    const labelUnidad = (u) => ([u.nombre, u.identificacion].filter(Boolean).join(' — ') || `Unidad ${u.id}`);
    const faltan = unidadTipo === 'asegurado' ? 'asegurados' : unidadTipo === 'ubicacion' ? 'ubicaciones' : 'equipos';
    return (<div className="form-group">
      <label className="form-label">{label}</label>
      <select className="form-select" value={v.unidad_id || ''}
        onChange={e => { const u = unidades.find(x => String(x.id) === String(e.target.value)); setMany({ unidad_id: e.target.value, unidad_label: u ? labelUnidad(u) : '' }); }}>
        <option value="">— Selecciona —</option>
        {unidades.map(u => <option key={u.id} value={u.id}>{labelUnidad(u)}</option>)}
      </select>
      {unidades.length === 0 && <div style={{ fontSize: 11, color: 'var(--text-warning)', marginTop: 4 }}>Esta póliza no tiene {faltan} cargados.</div>}
    </div>);
  }

  switch (target) {
    case 'vehiculo_inclusion':
      return (<div>
        <div style={{ fontSize: 12, color: 'var(--silver)', marginBottom: 8 }}>Datos del vehículo que se INCLUYE en la póliza:</div>
        <VehiculoFields />
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed rgba(var(--silver-rgb),0.15)' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--silver)', letterSpacing: 0.5, marginBottom: 6 }}>COBERTURAS DE ESTA UNIDAD</div>
          <CoberturaMiniList value={v.coberturas || []} onChange={cobs => set('coberturas', cobs)} />
        </div>
      </div>);

    case 'vehiculo_exclusion':
      return (<div><SelectorVehiculo label="Vehículo a EXCLUIR de la póliza" /></div>);

    case 'vehiculo_cambio':
      return (<div>
        <SelectorVehiculo label="Vehículo que se REEMPLAZA" />
        <div style={{ fontSize: 12, color: 'var(--silver)', margin: '10px 0 8px' }}>Datos del vehículo NUEVO:</div>
        <VehiculoFields />
      </div>);

    case 'vehiculo_datos':
      return (<div>
        <SelectorVehiculo label="Vehículo a corregir" />
        {v.vehiculo_id && (<><div style={{ fontSize: 12, color: 'var(--silver)', margin: '10px 0 8px' }}>Corrige los datos que cambian:</div><VehiculoFields /></>)}
      </div>);

    case 'suma_cambio':
      return (<div className="form-grid form-grid-3">
        <div className="form-group" style={{ gridColumn: 'span 2' }}>
          <label className="form-label">Cobertura</label>
          <select className="form-select" value={v.cobertura_id || ''} onChange={e => elegirCobertura(e.target.value)}>
            <option value="">— Selecciona la cobertura —</option>
            {coberturas.map(c => <option key={c.id} value={c.id}>{labelCobertura(c)}</option>)}
          </select>
        </div>
        <div className="form-group"><label className="form-label">Nueva suma asegurada</label>
          <input className="form-input" type="number" step="0.01" value={v.suma_asegurada || ''} onChange={e => set('suma_asegurada', e.target.value)} placeholder="0.00" /></div>
      </div>);

    case 'deducible_cambio':
      return (<div className="form-grid form-grid-3">
        <div className="form-group" style={{ gridColumn: 'span 2' }}>
          <label className="form-label">Cobertura</label>
          <select className="form-select" value={v.cobertura_id || ''} onChange={e => elegirCobertura(e.target.value)}>
            <option value="">— Selecciona la cobertura —</option>
            {coberturas.map(c => <option key={c.id} value={c.id}>{labelCobertura(c)}</option>)}
          </select>
          {coberturas.length === 0 && <div style={{ fontSize: 11, color: 'var(--text-warning)', marginTop: 4 }}>Esta póliza no tiene coberturas cargadas.</div>}
        </div>
        <div className="form-group"><label className="form-label">Nuevo deducible</label>
          <input className="form-input" value={v.deducible || ''} onChange={e => set('deducible', e.target.value)} placeholder="Ej: 10% mín. B/.250" /></div>
      </div>);

    case 'cobertura_inclusion':
      return (<div className="form-grid form-grid-3">
        <div className="form-group" style={{ gridColumn: 'span 2' }}><label className="form-label">Nombre de la cobertura</label>
          <input className="form-input" value={v.nombre || ''} onChange={e => set('nombre', e.target.value)} placeholder="Ej: Auto de reemplazo" /></div>
        <div className="form-group"><label className="form-label">Suma asegurada</label>
          <input className="form-input" type="number" step="0.01" value={v.suma_asegurada || ''} onChange={e => set('suma_asegurada', e.target.value)} placeholder="0.00" /></div>
        <div className="form-group" style={{ gridColumn: 'span 2' }}><label className="form-label">Deducible</label>
          <input className="form-input" value={v.deducible || ''} onChange={e => set('deducible', e.target.value)} placeholder="Estructura del deducible" /></div>
        <div className="form-group"><label className="form-label">Otros / etiqueta</label>
          <input className="form-input" value={v.otros || ''} onChange={e => set('otros', e.target.value)} placeholder="Ej: 5,000 / 10,000" /></div>
      </div>);

    case 'cobertura_exclusion':
      return (<div className="form-group">
        <label className="form-label">Cobertura a EXCLUIR</label>
        <select className="form-select" value={v.cobertura_id || ''} onChange={e => elegirCobertura(e.target.value)}>
          <option value="">— Selecciona la cobertura —</option>
          {coberturas.map(c => <option key={c.id} value={c.id}>{labelCobertura(c)}</option>)}
        </select>
        {coberturas.length === 0 && <div style={{ fontSize: 11, color: 'var(--text-warning)', marginTop: 4 }}>Esta póliza no tiene coberturas cargadas.</div>}
      </div>);

    case 'forma_pago':
      return (<div className="form-grid form-grid-3">
        <div className="form-group"><label className="form-label">Nueva forma de pago</label>
          <select className="form-select" value={v.forma_pago || ''} onChange={e => set('forma_pago', e.target.value)}>
            <option value="">— Sin cambio —</option>
            {FORMAS_PAGO.map(f => <option key={f} value={f}>{f}</option>)}
          </select></div>
        <div className="form-group"><label className="form-label">Nueva frecuencia</label>
          <select className="form-select" value={v.frecuencia_pago || ''} onChange={e => set('frecuencia_pago', e.target.value)}>
            <option value="">— Sin cambio —</option>
            {FRECUENCIAS.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
          </select></div>
      </div>);

    case 'fecha_pago':
      return (<div>
        <div className="form-grid form-grid-3">
          <div className="form-group"><label className="form-label">Nuevo día de pago (1–31)</label>
            <input className="form-input" type="number" min="1" max="31" value={v.dia_pago || ''}
              onChange={e => set('dia_pago', e.target.value)} placeholder="Ej: 15" /></div>
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--silver)', marginTop: 6 }}>
          Re-fecha las cuotas <strong>pendientes</strong> del plan al día indicado de cada mes (sin cambiar prima ni montos).
          Si el mes no tiene ese día (ej. 31 en febrero), usa el último día del mes.
        </div>
      </div>);

    case 'vigencia':
      return (<div className="form-grid form-grid-3">
        <div className="form-group"><label className="form-label">Nueva fecha de inicio</label>
          <input className="form-input" type="date" value={v.fecha_inicio || ''} onChange={e => set('fecha_inicio', e.target.value)} /></div>
        <div className="form-group"><label className="form-label">Nueva fecha de vencimiento</label>
          <input className="form-input" type="date" value={v.fecha_vencimiento || ''} onChange={e => set('fecha_vencimiento', e.target.value)} /></div>
      </div>);

    case 'datos_generales':
      return (<div className="form-grid form-grid-2">
        <div className="form-group" style={{ gridColumn: 'span 2' }}><label className="form-label">Descripción del riesgo</label>
          <input className="form-input" value={v.descripcion_riesgo ?? ''} onChange={e => set('descripcion_riesgo', e.target.value)} /></div>
        <div className="form-group"><label className="form-label">Beneficiario — nombre</label>
          <input className="form-input" value={v.beneficiario_nombre ?? ''} onChange={e => set('beneficiario_nombre', e.target.value)} /></div>
        <div className="form-group"><label className="form-label">Beneficiario — cédula/RUC</label>
          <input className="form-input" value={v.beneficiario_cedula ?? ''} onChange={e => set('beneficiario_cedula', e.target.value)} /></div>
        <div className="form-group"><label className="form-label">Asegurado — nombre</label>
          <input className="form-input" value={v.asegurado_nombre ?? ''} onChange={e => set('asegurado_nombre', e.target.value)} /></div>
        <div className="form-group"><label className="form-label">Asegurado — cédula</label>
          <input className="form-input" value={v.asegurado_cedula ?? ''} onChange={e => set('asegurado_cedula', e.target.value)} /></div>
        <div className="form-group"><label className="form-label">Tomador — nombre</label>
          <input className="form-input" value={v.tomador_nombre ?? ''} onChange={e => set('tomador_nombre', e.target.value)} /></div>
        <div className="form-group"><label className="form-label">Tomador — cédula</label>
          <input className="form-input" value={v.tomador_cedula ?? ''} onChange={e => set('tomador_cedula', e.target.value)} /></div>
      </div>);

    case 'fianza_datos':
      return (<div className="form-grid form-grid-2">
        <div className="form-group"><label className="form-label">Nuevo monto afianzado</label>
          <input className="form-input" type="number" step="0.01" value={v.monto_afianzado ?? ''} onChange={e => set('monto_afianzado', e.target.value)} placeholder="0.00" /></div>
        <div className="form-group"><label className="form-label">Nueva tarifa de la fianza (% anual)</label>
          <input className="form-input" type="number" step="0.01" value={v.tarifa_fianza ?? ''} onChange={e => set('tarifa_fianza', e.target.value)} placeholder="0.00" /></div>
        <div className="form-group" style={{ gridColumn: 'span 2' }}><label className="form-label">Beneficiario de la fianza</label>
          <input className="form-input" value={v.beneficiario_fianza ?? ''} onChange={e => set('beneficiario_fianza', e.target.value)} /></div>
      </div>);

    default:
      // libre / cancelacion / prorroga → sin captura estructural
      return null;
  }
}
